import React from 'react';
import {Pressable, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import {formatDate} from '@/Utils/CommonUtils';
import dayjs from 'dayjs';
import eco from '@/Assets/Eco/eco.json';
import customStyles from '../styles';
import {FavStar} from './FavStar';
import {AccuracyRing} from './AccuracyRing';
import {CardResultStrip} from './CardResultStrip';
import {BoardAccent} from './BoardAccent';
import {TimeClassPill} from './TimeClassPill';

const openingMap: Record<string, string> = {};
eco.map((e: any) => {
  openingMap[e.eco] = e.name;
});

// `name` always links to its profile via `ccUsername` -- own name
// (item.username) or the opponent's (item.opponent_username). Except Caissa
// (the dedicated practice/analysis bot, never a real CCUser) -- same
// 'caissa' substring rule dashboard/leaderboard_learning.py already uses.
const PlayerNameLink = ({
  name,
  ccUsername,
  navigation,
  platform,
}: {
  name: string;
  ccUsername?: string | null;
  navigation: any;
  // Only opponents of games PLAYED ON CIRCLECHESS are CCUsers with a
  // profile -- Chess.com/Lichess/OTB opponent names are platform handles
  // that won't resolve. Pass the game row's platform for the opponent
  // link; omit for the owner's own name (always a CCUser regardless of
  // where the game was played).
  platform?: string;
}) => {
  const nonCcPlatform = platform !== undefined && platform !== 'Circlechess';
  if (!ccUsername || nonCcPlatform || /caissa/i.test(ccUsername)) {
    return (
      <CCText style={customStyles.playerName} numberOfLines={1}>
        {name}
      </CCText>
    );
  }
  return (
    <Pressable
      testID={`games-list-player-link-${ccUsername}`}
      onPress={e => {
        e.stopPropagation?.();
        navigation?.navigate?.('PlayerProfile', {username: ccUsername});
      }}>
      <CCText
        style={[customStyles.playerName, customStyles.playerNameLink]}
        numberOfLines={1}>
        {name}
      </CCText>
    </Pressable>
  );
};

export const GameCard = ({
  item,
  isFav,
  onToggleFav,
  onPress,
  navigation,
}: {
  item: any;
  isFav: boolean;
  onToggleFav: () => void;
  onPress: () => void;
  navigation?: any;
}) => {
  const gameUuid = item.uuid;
  const timeClass = item?.time_class ?? 'rapid';
  const result = item.result?.toLowerCase() ?? 'draw';
  const opponent = item.opponent_username;
  const player = item.username;
  const playerRating = item.rating;
  const opponentRating = item.opponent_rating;
  const date = item.end_time;
  const platform = item.platform;
  const accuracy = item.accuracy ?? 0;
  const opponentAccuracy = item.opponent_accuracy ?? 0;
  const solved = item.solved_positions ?? 0;
  const total = item.total_positions ?? 0;
  const color = item.color ?? 'white';
  const pgnStr = typeof item.pgn === 'string' ? item.pgn : '';
  const ecoMatch = pgnStr.match(/\[ECO\s+"([^"]+)"\]/);
  const ecoCode = ecoMatch ? ecoMatch[1] : '';

  const rawOpening =
    item.opening && item.opening !== 'Unknown' ? item.opening : ecoCode;
  const isUnknown =
    !rawOpening || rawOpening.toLowerCase() === 'unknown' || rawOpening === '?';
  const opening = isUnknown ? '' : openingMap[rawOpening] ?? rawOpening ?? '';
  const tcStr = item?.time_control ?? '';
  const formattedTC = (() => {
    if (!tcStr) {
      return '';
    }
    const parts = tcStr.split('+');
    const baseSecs = parseInt(parts[0], 10);
    if (isNaN(baseSecs)) {
      return tcStr;
    }
    const mins = Math.floor(baseSecs / 60);
    const inc = parts[1] ? parseInt(parts[1], 10) : 0;
    return `${mins}+${inc}`;
  })();

  let btnStyle = customStyles.actionBtnView;
  let btnTextStyle = customStyles.actionBtnViewText;
  let btnLabel = 'View analysis';

  if (total > 0 && solved === total) {
    btnStyle = customStyles.actionBtnStudy;
    btnTextStyle = customStyles.actionBtnStudyText;
    btnLabel = 'Study again';
  }
  if (item.is_analyzing) {
    btnStyle = customStyles.actionBtnAnalyzing;
    btnTextStyle = customStyles.actionBtnAnalyzingText;
    btnLabel = 'Analyzing…';
  } else if (item.analyzed === 0) {
    btnStyle = customStyles.actionBtnRequest;
    btnTextStyle = customStyles.actionBtnRequestText;
    btnLabel = 'Request analysis';
  }

  const formatDateToLocal = (dateString: string) => {
    if (!dateString) {
      return '';
    }
    return formatDate(dateString, {
      timezone: dayjs.tz.guess(),
      format: '[ORDINAL] MMMM YYYY @ h:mm A',
    });
  };

  const dateString =
    platform !== 'OTB'
      ? formatDateToLocal(date)
      : formatDateToLocal(date).split('@')[0];
  const parts = dateString.split('@');
  const dPart = parts[0]?.trim();
  const tPart = parts[1]?.trim() || '';

  return (
    <Pressable
      onPress={onPress}
      style={({pressed}) => [
        customStyles.gameCard,
        isFav && customStyles.gameCardHighlight,
        pressed && customStyles.gameCardPressed,
      ]}>
      <CardResultStrip result={result} />
      <BoardAccent result={result} />

      <View style={customStyles.matchup}>
        <View style={customStyles.playersLine}>
          <View
            style={[
              customStyles.colorDot,
              color?.toLowerCase() === 'white' && customStyles.colorDotFilled,
            ]}
          />
          <PlayerNameLink name={player} ccUsername={item.username} navigation={navigation} />
          <CCText style={customStyles.playerRating}>({playerRating})</CCText>
          <CCText style={customStyles.vsSep}>vs</CCText>
          <View
            style={[
              customStyles.colorDot,
              color?.toLowerCase() !== 'white' && customStyles.colorDotFilled,
            ]}
          />
          <PlayerNameLink
            name={opponent}
            ccUsername={item.opponent_username}
            navigation={navigation}
            platform={item.platform}
          />
          <CCText style={customStyles.playerRating}>({opponentRating})</CCText>
        </View>
        <View style={customStyles.tagsRow}>
          <TimeClassPill timeClass={timeClass} timeControl={formattedTC} />
          <CCText style={customStyles.plainTag}>
            {platform === 'Circlechess' ? 'CircleChess' : platform}
          </CCText>
          {opening ? (
            <View style={[customStyles.tag, customStyles.openingTagBg]}>
              <CCText
                style={[customStyles.tagText, customStyles.openingTagText]}
                numberOfLines={1}>
                {opening}
              </CCText>
            </View>
          ) : null}
        </View>
      </View>

      {/* Divider 1 separating Matchup from Accuracy */}
      <View style={customStyles.verticalDivider} />

      {/* Accuracy Section (User + Opponent Accuracy) */}
      <View style={customStyles.accuracyCol}>
        {accuracy > 0 || opponentAccuracy > 0 ? (
          <>
            <CCText style={customStyles.accuracyHeaderText}>Accuracy</CCText>
            <View style={customStyles.accuracyRow}>
              <AccuracyRing accuracy={accuracy} label="YOU" />
              <CCText style={customStyles.accuracyVsText}>vs</CCText>
              <AccuracyRing accuracy={opponentAccuracy} label="OPP" />
            </View>
          </>
        ) : (
          <View style={customStyles.accuracyWrapper}>
            <CCText style={customStyles.notAnalyzedText}>
              Not{'\n'}analyzed
            </CCText>
          </View>
        )}
      </View>

      {/* Divider 2 separating Accuracy from Date */}
      <View style={customStyles.verticalDivider} />

      {/* Date Column */}
      <View style={customStyles.dateCol}>
        <CCText style={customStyles.dateHeaderText}>Date</CCText>
        <CCText style={customStyles.dateMain} numberOfLines={1}>
          {dPart}
        </CCText>
        {tPart ? <CCText style={customStyles.dateSub}>{tPart}</CCText> : null}
      </View>

      {/* Divider 3 separating Date from Puzzles */}
      <View style={customStyles.verticalDivider} />

      {/* Puzzles Column */}
      <View style={customStyles.puzzleCol}>
        <CCText style={customStyles.puzzleHeaderText}>Puzzles</CCText>
        {total > 0 ? (
          <>
            <CCText style={customStyles.puzzleLabel}>
              {solved}/{total} puzzles
            </CCText>
            <View style={customStyles.progressTrack}>
              <View
                style={[
                  customStyles.progressFill,
                  {width: `${(solved / total) * 100}%`},
                ]}
              />
            </View>
            <CCText style={customStyles.puzzlePct}>
              {Math.round((solved / total) * 100)}%
            </CCText>
          </>
        ) : (
          <CCText style={customStyles.puzzleLabelMuted}>No puzzles</CCText>
        )}
      </View>

      {/* Divider 4 separating Puzzles from Actions */}
      <View style={customStyles.verticalDivider} />

      <FavStar
        isFav={isFav}
        gameUuid={gameUuid}
        callBack={onToggleFav}
        style={{marginLeft: -4, marginRight: 24}}
      />

      {/* Actions Column */}
      <View style={customStyles.actionCol}>
        <Pressable
          onPress={e => {
            e.stopPropagation?.();
            onPress();
          }}
          style={[customStyles.actionBtn, btnStyle]}>
          <CCText style={[customStyles.actionBtnBase, btnTextStyle]}>
            {btnLabel}
          </CCText>
        </Pressable>
      </View>
    </Pressable>
  );
};

export default GameCard;
